import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd'
import dayjs from 'dayjs'
import api from '../../utils/api'
import Hint from '../../components/common/Hint'
import { fromPicker } from '../../utils/timezone'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

// ── 計畫期間 ────────────────────────────────────────────────────
/**
 * 核銷用的查詢區間必須是固定值，不能跟著「今天」跑。
 * 先前結束日寫成 dayjs()，同一份報表隔天打開數字就不一樣，
 * 也無法跟已送件的 Excel 對帳（服務日期落在 9/1 之後的單會被算進來）。
 * 要看計畫期間之後的營運狀況，改用日期選擇器手動調整即可。
 */
const PROGRAM_START = '2025-10-01'
const PROGRAM_END = '2026-08-31'

const moneyFmt = (v) =>
  v === null || v === undefined || v === '—'
    ? '—'
    : Number(v).toLocaleString('zh-TW')

// ── 問卷 → 基準欄位換算 ──────────────────────────────────────────
const toNum = (x) =>
  x === null || x === undefined || x === '' || Number.isNaN(Number(x))
    ? null
    : Number(x)
const round1 = (x) => (x === null ? null : Math.round(x * 10) / 10)

// 問卷 Q4-(6) 區間 → 分鐘數（取中位數，同問卷附錄原則）
const PROCESS_BAND_OPTIONS = [
  { value: 3, label: '5 分內（以 3 分計）' },
  { value: 10, label: '5–15 分（以 10 分計）' },
  { value: 22.5, label: '15–30 分（以 22.5 分計）' },
  { value: 45, label: '30–60 分（以 45 分計）' },
  { value: 90, label: '超過 60 分（以 90 分計）' }
]

/** 把「美業店家回饋問卷」原始作答換算成 KpiBaseline 欄位 */
const deriveBaseline = (v = {}) => {
  const q1 = toNum(v.q4_1) // 每月預約組數
  const q2 = toNum(v.q4_2) // 爽約組數
  const q3g = toNum(v.q4_3_groups) // 到店完成組數
  const q3f = toNum(v.q4_3_visits) // 總人次
  const q4 = toNum(v.q4_4) // 回流比例 %
  const q5s = toNum(v.q4_5) // 成功約成比例 %
  const q6 = toNum(v.q4_6) // 流程時間（分，區間中位數）
  const q7h = toNum(v.q4_7_hours) // 小時/天
  const q7d = toNum(v.q4_7_days) // 天/月
  const q8 = toNum(v.q4_8) // 空檔比例 %
  const q9 = toNum(v.q4_9) // 月營業額
  const a5h = toNum(v.q5_hours) // 導入後 小時/天
  const a5d = toNum(v.q5_days) // 導入後 天/月

  return {
    bookingsPerMonth: q1,
    noShowRate: q1 > 0 && q2 !== null ? round1((q2 / q1) * 100) : null,
    arrivalRate: q1 > 0 && q3g !== null ? round1((q3g / q1) * 100) : null,
    footTrafficPerMonth: q3f,
    returnRate: q4,
    bookingSuccessRate: q5s,
    avgBookingProcessMinutes: q6,
    laborHoursPerMonth:
      q7h !== null && q7d !== null ? round1(q7h * q7d) : null,
    idleSlotRatio: q8,
    utilizationRate: q8 !== null ? round1(100 - q8) : null,
    monthlyRevenue: q9,
    laborHoursPerMonthAfter:
      a5h !== null && a5d !== null ? round1(a5h * a5d) : null
  }
}

const KpiReportPage = () => {
  const { message } = App.useApp()
  // 預設查詢範圍：計畫期間（固定區間，數字才可重現、可稽核）
  const [range, setRange] = useState([
    dayjs(PROGRAM_START).startOf('day'),
    dayjs(PROGRAM_END).endOf('day')
  ])
  const [scope, setScope] = useState('platform') // platform | <businessId>
  // 展示用店家（seed:sim2026）與模擬填答預設一併計入；切成「只計真實營運」時排除
  const [excludeSimulation, setExcludeSimulation] = useState(false)
  const [businesses, setBusinesses] = useState([])
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  const [baselineOpen, setBaselineOpen] = useState(false)
  const [baselineForm] = Form.useForm()
  const [manualOpen, setManualOpen] = useState(false)
  const [manualForm] = Form.useForm()

  // 載入店家清單（供下鑽選擇）
  useEffect(() => {
    api
      .get('/api/admin/businesses', { params: { limit: 100 } })
      .then((res) => {
        if (res.data?.success) setBusinesses(res.data.data || [])
      })
      .catch(() => {})
  }, [])

  const fetchSummary = useCallback(async () => {
    if (!range?.[0] || !range?.[1]) return
    setLoading(true)
    try {
      const params = {
        periodStart: fromPicker(range[0]).toISOString(),
        periodEnd: fromPicker(range[1]).toISOString()
      }
      if (scope !== 'platform') params.businessId = scope
      if (excludeSimulation) params.excludeSimulation = 'true'
      const res = await api.get('/api/admin/kpi/summary', { params })
      if (res.data?.success) setData(res.data.data)
      else message.error(res.data?.message || '載入失敗')
    } catch (e) {
      message.error(e.response?.data?.message || '載入 KPI 失敗')
    } finally {
      setLoading(false)
    }
  }, [range, scope, excludeSimulation, message])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  const exportExcel = async () => {
    try {
      const params = {
        periodStart: fromPicker(range[0]).toISOString(),
        periodEnd: fromPicker(range[1]).toISOString()
      }
      if (scope !== 'platform') params.businessId = scope
      if (excludeSimulation) params.excludeSimulation = 'true'
      const res = await api.get('/api/admin/kpi/export', {
        params,
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `KPI報表_${range[0].format('YYYYMMDD')}_${range[1].format(
        'YYYYMMDD'
      )}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      message.error('匯出失敗')
    }
  }

  // 登錄問卷資料（單店；照問卷題目填，系統自動換算基準）
  const openBaseline = () => {
    if (scope === 'platform') {
      message.info('請先在上方選擇單一店家，再登錄該店的問卷資料')
      return
    }
    const b = data?.baseline || {}
    baselineForm.resetFields()
    baselineForm.setFieldsValue({ ...(b.surveyRaw || {}), notes: b.notes })
    setBaselineOpen(true)
  }

  const saveBaseline = async () => {
    const v = await baselineForm.validateFields()
    const biz = businesses.find((x) => x._id === scope)
    await api.put('/api/admin/kpi/baselines', {
      businessId: scope,
      businessName: biz?.name,
      surveyRaw: v,
      notes: v.notes,
      source: 'survey',
      ...deriveBaseline(v)
    })
    message.success('已儲存問卷資料（系統已自動換算為 KPI 基準）')
    setBaselineOpen(false)
    fetchSummary()
  }

  const openManual = () => {
    const m = data?.platformManual || {}
    manualForm.setFieldsValue({
      employmentAdded: m.employmentAdded,
      surveysCollected: m.surveysCollected,
      revenue401: m.revenue401
    })
    setManualOpen(true)
  }

  const saveManual = async () => {
    const v = await manualForm.validateFields()
    await api.put('/api/admin/kpi/baselines', { platformLevel: true, ...v })
    message.success('已儲存平台行政指標')
    setManualOpen(false)
    fetchSummary()
  }

  const totals = data?.totals || {}

  // ── KPI 表格欄位（三張表共用的欄位元件） ──
  const colItem = { title: '項次', dataIndex: 'item', key: 'item', width: 70 }
  const colName = { title: 'KPI', dataIndex: 'name', key: 'name' }
  const colTarget = {
    title: '目標',
    key: 'target',
    render: (_, r) =>
      `${r.unit === '元' ? moneyFmt(r.target) : r.target}${
        ['%'].includes(r.unit) ? '%' : ` ${r.unit}`
      }`
  }
  const colActual = (title) => ({
    title,
    key: 'actual',
    render: (_, r) =>
      r.actual === null || r.actual === undefined ? (
        <Text type='secondary'>—</Text>
      ) : r.unit === '元' ? (
        moneyFmt(r.actual)
      ) : r.actualUnit ? (
        `${r.actual} ${r.actualUnit}`
      ) : (
        `${r.actual}${r.unit === '%' ? '%' : ''}`
      )
  })
  const colBaseline = {
    title: '導入前基準',
    key: 'baseline',
    render: (_, r) =>
      r.baseline === null || r.baseline === undefined ? (
        <Text type='secondary'>—</Text>
      ) : r.unit === '元' ? (
        moneyFmt(r.baseline)
      ) : r.actualUnit ? (
        `${r.baseline} ${r.actualUnit}`
      ) : (
        `${r.baseline}${r.unit === '%' ? '%' : ''}`
      )
  }
  const colImprovement = {
    title: '提升/降低',
    key: 'improvementPct',
    render: (_, r) => {
      if (r.improvementPct === null || r.improvementPct === undefined)
        return <Text type='secondary'>—</Text>
      if (r.type === 'revenueDelta')
        return <Text strong>{moneyFmt(r.improvementPct)} 元</Text>
      return <Text strong>{r.improvementPct}%</Text>
    }
  }
  const colAchieved = {
    title: '達成',
    key: 'achieved',
    render: (_, r) =>
      r.achieved ? (
        <Tag color='success'>達成</Tag>
      ) : (
        <Tag color={r.actual === null ? 'default' : 'error'}>
          {r.actual === null ? '待補' : '未達'}
        </Tag>
      )
  }
  const colSource = {
    title: '資料來源',
    dataIndex: 'source',
    key: 'source',
    render: (t, r) => (
      <Space direction='vertical' size={0}>
        <Text style={{ fontSize: 12 }}>{t}</Text>
        {r.note && (
          <Text type='secondary' style={{ fontSize: 11 }}>
            {r.note}
          </Text>
        )}
      </Space>
    )
  }

  // 一、計畫期間累計：無前後比較概念，欄位精簡
  const cumulativeColumns = [
    colItem,
    colName,
    colTarget,
    colActual('實際累計'),
    colAchieved,
    colSource
  ]
  // 二、導入前後比較：完整欄位
  const comparisonColumns = [
    colItem,
    colName,
    colTarget,
    colActual('導入後實際'),
    colBaseline,
    colImprovement,
    colAchieved,
    colSource
  ]
  // 三、行政與問卷（人工填報）
  const manualColumns = [
    colItem,
    colName,
    colTarget,
    colActual('實際'),
    colAchieved,
    colSource
  ]

  /**
   * 單店明細排序：有數據的店家排前面。
   *
   * 平台上大多數店家在任一區間內都是 0 筆預約，若照 API 回傳的自然順序
   * 顯示，第一頁幾乎整頁空白，要翻好幾頁才看得到真正在用的店。
   *
   * 後端 utils/kpiMetrics.js 的 computeKpi 已用同一套規則排過（Excel 匯出
   * 吃的是那份），這裡再排一次是為了讓舊版後端也能正確顯示，順序兩邊一致。
   * 排序鍵：有無預約 → 預約量 → 成交額 → 店名（同分時順序才穩定）。
   */
  const sortedStores = useMemo(() => {
    return [...(data?.stores || [])].sort((a, b) => {
      const aHas = (a.bookingCount || 0) > 0 ? 1 : 0
      const bHas = (b.bookingCount || 0) > 0 ? 1 : 0
      if (aHas !== bHas) return bHas - aHas
      if ((b.bookingCount || 0) !== (a.bookingCount || 0)) {
        return (b.bookingCount || 0) - (a.bookingCount || 0)
      }
      if ((b.revenue || 0) !== (a.revenue || 0)) {
        return (b.revenue || 0) - (a.revenue || 0)
      }
      return String(a.name || '').localeCompare(String(b.name || ''), 'zh-Hant')
    })
  }, [data])

  const storeColumns = [
    { title: '店家', dataIndex: 'name', key: 'name', fixed: 'left', width: 160 },
    { title: '預約量', dataIndex: 'bookingCount', key: 'bookingCount' },
    { title: '完成率%', dataIndex: 'completionRate', key: 'completionRate' },
    { title: '爽約率%', dataIndex: 'noShowRate', key: 'noShowRate' },
    { title: '到店率%', dataIndex: 'arrivalRate', key: 'arrivalRate' },
    { title: '回流率%', dataIndex: 'returnRate', key: 'returnRate' },
    { title: '客流人次', dataIndex: 'footTraffic', key: 'footTraffic' },
    {
      title: '成交額(GMV)',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (v) => moneyFmt(v)
    },
    {
      title: '月均成交額',
      dataIndex: 'monthlyRevenue',
      key: 'monthlyRevenue',
      render: (v) => moneyFmt(v)
    },
    { title: '工時利用率%', dataIndex: 'utilizationRate', key: 'utilizationRate' },
    { title: '空檔占比%', dataIndex: 'idleSlotRatio', key: 'idleSlotRatio' }
  ]

  return (
    <Card>
      <Title level={2} style={{ marginBottom: 4 }}>
        試營運 KPI 成效報表
      </Title>
      <Text type='secondary'>
        導入後數據由後台自動計算；導入前基準請選擇單一店家後，用「登錄此店問卷資料」照問卷數字填入，系統會自動換算並算出提升／降低 %。
      </Text>

      <Space wrap style={{ margin: '16px 0' }}>
        <RangePicker
          value={range}
          onChange={(v) => v && setRange(v)}
          allowClear={false}
        />
        <Select
          style={{ width: 220 }}
          value={scope}
          onChange={setScope}
          showSearch
          optionFilterProp='label'
          options={[
            { value: 'platform', label: '平台整體' },
            ...businesses.map((b) => ({
              value: b._id,
              label: b.name || b.username || b._id
            }))
          ]}
        />
        {/* {scope === 'platform' && (
          <Segmented
            value={excludeSimulation ? 'real' : 'all'}
            onChange={(v) => setExcludeSimulation(v === 'real')}
            options={[
              { value: 'all', label: '含展示店家' },
              { value: 'real', label: '只計真實營運' }
            ]}
          />
        )} */}
        <Button type='primary' onClick={fetchSummary} loading={loading}>
          查詢
        </Button>
        <Button onClick={exportExcel}>匯出 Excel</Button>
        {scope === 'platform' ? (
          <Button onClick={openManual}>編輯平台行政指標</Button>
        ) : (
          <Button onClick={openBaseline}>登錄此店問卷資料</Button>
        )}
      </Space>

      {data && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card size='small'>
                <Statistic title='預約單量' value={totals.bookingCount ?? 0} suffix='筆' />
              </Card>
            </Col>
            <Col span={6}>
              <Card size='small'>
                <Statistic
                  title='預約完成率'
                  value={totals.completionRate ?? 0}
                  suffix='%'
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size='small'>
                <Statistic
                  title='期間店家成交額（GMV，非公司營業額）'
                  value={totals.revenue ?? 0}
                  formatter={(v) => moneyFmt(v)}
                  suffix='元'
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size='small'>
                <Statistic
                  title={scope === 'platform' ? '新增店家／會員' : '客流人次'}
                  value={
                    scope === 'platform'
                      ? `${totals.newStores ?? 0} / ${totals.newMembers ?? 0}`
                      : totals.footTraffic ?? 0
                  }
                />
              </Card>
            </Col>
          </Row>

          {(() => {
            const allRows = data.kpiRows || []
            const rowKeyFn = (r) => (r.item || r.group || '') + r.name
            // 後端尚未更新（無 section 欄位）時退回單一總表
            if (!allRows.some((r) => r.section)) {
              return (
                <>
                  <Title level={4}>KPI 達成總表</Title>
                  <Table
                    rowKey={rowKeyFn}
                    columns={comparisonColumns}
                    dataSource={allRows}
                    pagination={false}
                    size='small'
                    loading={loading}
                  />
                </>
              )
            }
            const bySection = (s) => allRows.filter((r) => r.section === s)
            return (
              <>
                <Title level={4}>一、計畫期間累計指標（後台數據佐證）</Title>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  統計區間＝上方查詢範圍，為整段期間累計值，無導入前後比較。
                </Text>
                <Table
                  rowKey={rowKeyFn}
                  columns={cumulativeColumns}
                  dataSource={bySection('cumulative')}
                  pagination={false}
                  size='small'
                  loading={loading}
                  style={{ marginTop: 8, marginBottom: 24 }}
                />

                <Title level={4}>二、導入前後比較指標（導入前＝店家問卷基準）</Title>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  {scope === 'platform' &&
                    `「導入後實際」僅統計已登錄問卷基準的店家（${
                      data.comparisonStoreCount ?? '—'
                    } 家），與導入前問卷基準同一母體；`}
                  計畫書口徑為「導入前三個月 vs
                  導入後三個月」，正式核銷時請將上方查詢範圍設定為三個月區間再匯出。提升／降低皆為相對幅度（(前−後)÷前）。
                </Text>
                <Table
                  rowKey={rowKeyFn}
                  columns={comparisonColumns}
                  dataSource={bySection('comparison')}
                  pagination={false}
                  size='small'
                  loading={loading}
                  style={{ marginTop: 8, marginBottom: 24 }}
                />

                <Title level={4}>三、行政與問卷回收（人工填報）</Title>
                <Table
                  rowKey={rowKeyFn}
                  columns={manualColumns}
                  dataSource={bySection('manual')}
                  pagination={false}
                  size='small'
                  loading={loading}
                  style={{ marginTop: 8 }}
                />
              </>
            )
          })()}

          <Divider />
          <Title level={4}>
            單店明細（{sortedStores.length} 家）
          </Title>
          <Table
            rowKey='businessId'
            columns={storeColumns}
            dataSource={sortedStores}
            scroll={{ x: 1100 }}
            size='small'
            pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 家` }}
          />
        </>
      )}

      {/* 登錄問卷資料（單店）：照「美業店家回饋問卷」題目填寫 */}
      <Modal
        title='登錄問卷資料（此店）'
        open={baselineOpen}
        onOk={saveBaseline}
        onCancel={() => setBaselineOpen(false)}
        width={640}
        destroyOnClose
      >
        <Hint
          tone='warning'
          style={{ marginBottom: 12 }}
          title='照「美業店家回饋問卷」上的數字直接抄進來即可，爽約率、到店率等比率由系統自動換算（見下方換算結果）。問卷沒填的小題留空，該 KPI 會顯示「待補」。'
        />
        <Form form={baselineForm} layout='vertical'>
          <Divider orientation='left' plain>
            問卷第二部分：導入平台【前】（Q4，前 3 個月平均）
          </Divider>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name='q4_1' label='(1) 每月預約「組數」（組／月）'>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='q4_2'
                label='(2) 其中爽約—沒來也沒取消（組／月）'
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='q4_3_groups'
                label='(3) 實際到店完成服務（組／月）'
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='q4_3_visits' label='(3) 總人次（人次／月）'>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='q4_4'
                label='(4) 90 天內回來消費第 2 次以上的顧客比例（%）'
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='q4_5'
                label='(5) 客人詢問後，最終成功約成的比例（%）'
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name='q4_6'
                label='(6) 從詢問到確認預約，平均花費時間（勾哪格選哪格）'
              >
                <Select
                  allowClear
                  placeholder='選擇問卷勾選的區間'
                  options={PROCESS_BAND_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='q4_7_hours'
                label='(7) 每天處理預約事務的時間（小時／天）'
              >
                <InputNumber min={0} max={24} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='q4_7_days' label='(7) 每月營業（天）'>
                <InputNumber min={0} max={31} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='q4_8'
                label='(8) 設計師可服務時段中「空著」的比例（%）'
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='q4_9' label='(9) 每月營業額（元／月）'>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation='left' plain>
            問卷第三部分：導入平台【後】（Q5，近 3 個月）
          </Divider>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name='q5_hours'
                label='導入後每天處理預約事務的時間（小時／天）'
              >
                <InputNumber min={0} max={24} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='q5_days' label='導入後每月營業（天）'>
                <InputNumber min={0} max={31} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name='notes' label='備註（問卷編號、填寫日期等）'>
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {() => {
              const d = deriveBaseline(baselineForm.getFieldsValue())
              const fmt = (v, unit = '') =>
                v === null || v === undefined ? '待補' : `${v}${unit}`
              return (
                <Hint
                  tone='success'
                  title='系統自動換算結果（儲存後寫入 KPI 基準）'
                >
                  {
                    <Text style={{ fontSize: 12 }}>
                      爽約率 {fmt(d.noShowRate, '%')}｜到店率{' '}
                      {fmt(d.arrivalRate, '%')}｜工時利用率{' '}
                      {fmt(d.utilizationRate, '%')}｜導入前月工時{' '}
                      {fmt(d.laborHoursPerMonth, ' 小時')}｜導入後月工時{' '}
                      {fmt(d.laborHoursPerMonthAfter, ' 小時')}
                    </Text>
                  }
                </Hint>
              )
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* 平台行政指標 */}
      <Modal
        title='編輯平台行政指標'
        open={manualOpen}
        onOk={saveManual}
        onCancel={() => setManualOpen(false)}
        destroyOnClose
      >
        <Form form={manualForm} layout='vertical'>
          <Form.Item name='employmentAdded' label='計畫期間增加就業人數（位）'>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name='surveysCollected' label='已回收美業店家回饋問卷（份）'>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name='revenue401'
            label='計畫期間公司營業額（元，依 401 申報書填報）'
            extra='結案佐證以 401 申報數字為準；系統統計之店家成交額（GMV）僅供參考，兩者不可混用'
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}

export default KpiReportPage
